<template>
  <RequestWizardStep name="Request Details" :complete="isComplete">
    <VeeValidateWrapper
      @validation-failed="isValid = false"
      @validation-passed="isValid = true"
      slim
    >
      <ContentCard title="Now tell us about the records you need.">
        <p class="lead mt-3">
          Please provide a description of the type of records you are seeking:
        </p>

        <StepSelection>
          <TextArea
            label="Request Description"
            rows="3"
            v-model="comments"
            :rules="{
              required: true,
              max: 2500
            }"
            :validationAttrs="{ mode: 'aggressive' }"
          />
        </StepSelection>
      </ContentCard>

      <ContentCard
        :title="'Please upload ' +
          (isGeneral
            ? 'any additional supporting information.'
            : 'the required documentation which authorizes you' +
              ' to obtain the requested records.')"
      >
        <template v-if="isFirstParty">
          <AlertMessage type="info" class="mt-4" slim>
            When you are seeking records about yourself, additional documentation is required per
            <a href="https://www.ecfr.gov/current/title-45/subtitle-A/subchapter-A/part-5b#p-5b.5(b)(2)"
              target="_blank"
            >Privacy Act Regulation 45 C.F.R. § 5b.5(b)(2)</a>.
          </AlertMessage>

          <p class="lead">
            Please upload a copy of one of the following as proof of identity:
          </p>

          <StepSelection>
            <ul>
              <li>Driver's license</li>
              <li>Passport</li>
              <li>Alien or voter registration card</li>
              <li>Union card</li>
            </ul>
          </StepSelection>
        </template>

        <template v-if="isThirdParty">
          <AlertMessage type="info" class="mt-4" slim>
            When you are seeking records on behalf of another individual, additional documentation
            is required per
            <a href="https://www.ecfr.gov/current/title-45/subtitle-A/subchapter-A/part-5#p-5.22(g)"
              target="_blank"
            >Privacy Act Regulation 44 C.F.R. § 5.22(g)</a>.
          </AlertMessage>

          <p class="lead">
            Please upload one of the following:
          </p>

          <StepSelection>
            <ul>
              <li>Notarized authorization</li>
              <li>Declaration</li>
              <li>Obituary (in the case of a deceased individual)</li>
              <li>Death Certificate (in case of a deceased individual)</li>
            </ul>
          </StepSelection>
        </template>

        <div class="fst-italic text-muted mb-4">
          <span class="bi bi-exclamation-circle"></span>
          <span v-if="!isGeneral"> You are required to upload at least one attachment.</span>
          You can upload multiple files, but
          the cumulative file size cannot exceed 20 MB. Only the following file types are allowed:
          gif, jpg, jpeg, png, txt, pdf, doc, docx.
        </div>

        <FileAttachments v-model="attachments"
          class="mb-2 mt-4"
          attachButtonClass="btn btn-outline-primary w-100 py-3 mb-3"
          :processNewFile="() => filesProcessing++"
          :rules="{ required: !isGeneral }"
          :validationAttrs="{
            customMessages: { required: 'Please add at least one attachment.' }
          }"
          @processing-complete="filesProcessing--"
          @processing-failed="filesProcessing--"
          ref="attachments"
        >
          <template #attach-button-label>
            <span class="bi bi-cloud-arrow-up"></span> Upload documentation...
          </template>
        </FileAttachments>

        <StepSelection v-if="!isGeneral">
          <CheckBox v-model="requiredDocumentationIncluded"
            :required="!isGeneral"
          >
            I certify that this request includes all required documentation
            which authorizes me to obtain these records.</CheckBox>
        </StepSelection>
      </ContentCard>
    </VeeValidateWrapper>
  </RequestWizardStep>
</template>

<script>
import subComponent from '@ssi/vue-common/src/mixins/subComponent';
import AlertMessage from '@/components/AlertMessage';
import { TextArea } from '@/components/formFields/common';
import FileAttachments from '@/components/formFields/FileAttachments';
import CheckBox from '@/components/formFields/CheckBox';
import VeeValidateWrapper from '@/components/VeeValidateWrapper';
import RequestWizardStep from '@/components/requestWizard/RequestWizardStep';
import StepSelection from '@/components/requestWizard/StepSelection';

export default {
  mixins: [subComponent],

  components: {
    AlertMessage,
    CheckBox,
    FileAttachments,
    RequestWizardStep,
    StepSelection,
    TextArea,
    VeeValidateWrapper
  },

  props: {
    requestType: {
      type: String,
      default: () => null
    }
  },

  data: () => ({
    filesProcessing: 0,
    isValid: false,
    requiredDocumentationIncluded: false
  }),

  computed: {
    isComplete() {
      return this.isValid && this.filesProcessing === 0;
    },

    isGeneral: ({ requestType }) => requestType === 'general',
    isFirstParty: ({ requestType }) => requestType === 'firstParty',
    isThirdParty: ({ requestType }) => requestType === 'thirdParty'
  },

  watch: {
    requiredDocumentationIncluded(val) {
      if (val) {
        this.$refs.attachments.validate();
      }
    }
  }
};
</script>
